import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import Modal from '@atlaskit/modal-dialog';

import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Alert from 'kingpin/Alert';

import AnalyticsContext from 'contexts/AnalyticsContext';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import RolesContext from 'contexts/RolesContext';
import ToastContext from 'contexts/ToastContext';
import WallboardsContext from 'contexts/WallboardsContext';
import CloseModal from 'components/CloseModal';
import FlexCentered from 'components/Common/FlexCentered';
import Row from 'components/Common/Row';
import Loading from 'components/Loading';
import PERMISSIONS from 'permissionDefinitions';
import useConfirmation from 'screens/DataManager/DatasetDefinitions/DatasetsIndex/PerformanceDatasetWizard/useConfirmation';

import patchConfiguration from '../api/patchConfiguration';
import postActivate from '../api/postActivate';
import postApprove from '../api/postApprove';
import ConfigureMicrosoftSsoContext from '../contexts/ConfigureMicrosoftSsoContext';
import useAvailableGroups from '../hooks/useAvailableGroups';
import getPopupMode, { PopupAction } from './getPopupMode';
import GroupInputCard from './GroupInputCard';
import Heading from './Heading';
import UsersGrid from './UsersGrid';

const ContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 40px;
  flex: 1;
  height: 100%;
  position: relative;
`;

const ManageGroupsPopupContainer = ({
  close,
  initialGroups,
  refreshConfig,
  status,
}: {
  close: () => void;
  initialGroups: MicrosoftSso.Group[];
  refreshConfig: () => void;
  status: MicrosoftSso.MicrosoftConfig['status'];
}) => {
  const isSsoActive = status === 'active';
  const {
    ConfirmationModal: UnsavedChangesConfirmationModal,
    getConfirmation: getCloseConfirmation,
  } = useConfirmation({
    title: 'Unsaved Changes',
    body:
      'If you navigate away, all changes made will be lost. Are you sure' +
      ' you want to discard these changes?',
    confirmText: 'Yes, Discard Changes',
  });
  const {
    ConfirmationModal: ApproveConfirmationModal,
    getConfirmation: getApproveConfirmation,
  } = useConfirmation({
    title: 'Approve SSO groups',
    body:
      'The customer will be able to activate SSO with these groups. Nothing' +
      ' changes until they do.',
    confirmText: 'Approve',
  });
  const {
    ConfirmationModal: ActivateConfirmationModal,
    getConfirmation: getActivateConfirmation,
  } = useConfirmation({
    title: 'Activate SSO',
    body:
      "Activating ends everyone's current sessions and switches this account" +
      ' to Microsoft-only sign-in. Users outside these groups will lose' +
      ' access. Are you sure you want to activate SSO now?',
    confirmText: 'Activate SSO',
  });
  const { availableGroups, isLoading } = useAvailableGroups();
  const { api } = useContext(CloudFunctionClientContext);
  const { currentPermissions } = useContext(RolesContext);
  const { showToast } = useContext(ToastContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { wallBoards } = useContext(WallboardsContext);

  const [selectedGroups, setSelectedGroups] = useState<MicrosoftSso.Group[]>(
    () => initialGroups,
  );
  const [isValid, setIsValid] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const hasChanges = useMemo(
    () =>
      !_.isEqual(
        selectedGroups.map((g) => g.id),
        initialGroups.map((g) => g.id),
      ),
    [initialGroups, selectedGroups],
  );
  const mode = getPopupMode({
    status,
    hasChanges,
    canApprove: currentPermissions.includes(
      PERMISSIONS.INTERNAL_USE.APPROVE_SSO,
    ),
  });

  useEffect(() => {
    trackEvent('SSO Configurations - Microsoft - Opened');
  }, [trackEvent]);

  const save = useCallback(
    (action: PopupAction): Promise<boolean> => {
      switch (action) {
        case 'approve':
          return postApprove(api);
        case 'activate':
          return postActivate(api);
        default:
          return patchConfiguration(
            api,
            selectedGroups.map((g) => g.id),
          );
      }
    },
    [api, selectedGroups],
  );

  const onSaved = useCallback(
    (action: PopupAction) => {
      switch (action) {
        case 'approve':
          trackEvent('SSO Configurations - Microsoft - Approved');
          showToast('Approved. The customer can now activate SSO');
          break;
        case 'activate':
          trackEvent('SSO Configurations - Microsoft - Activated');
          showToast('Activating SSO');
          break;
        case 'submit':
          trackEvent('SSO Configurations - Microsoft - Submitted for review');
          showToast('Submitted for FleetOps review');
          break;
        case 'sync':
          trackEvent('SSO Configurations - Microsoft - Clicked Activate');
          showToast('Sync in progress');
          break;
      }
    },
    [showToast, trackEvent],
  );

  const onActionClicked = useCallback(
    async (action: PopupAction) => {
      if (!isValid) {
        return;
      }

      if (action === 'approve' && !(await getApproveConfirmation())) {
        return;
      }
      if (action === 'activate' && !(await getActivateConfirmation())) {
        return;
      }

      setIsSaving(true);
      const isOk = await save(action);
      setIsSaving(false);
      if (isOk) {
        onSaved(action);
        refreshConfig();
        close();
      } else {
        alert('Something has gone wrong');
      }
    },
    [
      close,
      getActivateConfirmation,
      getApproveConfirmation,
      isValid,
      onSaved,
      refreshConfig,
      save,
    ],
  );

  const onCloseClicked = useCallback(() => {
    if (hasChanges) {
      getCloseConfirmation().then((isConfirmed) => {
        trackEvent('SSO Configurations - Microsoft - Discarded changes');
        if (isConfirmed) {
          close();
        }
      });
    } else {
      close();
    }
  }, [close, getCloseConfirmation, hasChanges, trackEvent]);

  return (
    <ConfigureMicrosoftSsoContext.Provider
      value={{
        initialGroups,
        selectedGroups,
        setSelectedGroups,
        availableGroups,
        setIsValid,
      }}
    >
      <Modal
        onClose={isSaving ? undefined : onCloseClicked}
        shouldScrollInViewport={false}
        autoFocus={false}
        width={'70vw'}
        height={'85vh'}
      >
        <ContentDiv>
          {(isSaving || isLoading) && (
            <FlexCentered>
              <Loading />
            </FlexCentered>
          )}
          {!(isSaving || isLoading) && (
            <>
              <CloseModal close={onCloseClicked} />
              {/* Pinned above the scrolling content, like the buttons below */}
              <Heading step={mode.step} />

              <div
                style={{
                  overflowY: 'auto',
                  marginBottom: 24,
                  display: 'flex',
                  flex: 1,
                  flexDirection: 'column',
                  position: 'relative',
                }}
              >
                {!isSsoActive && wallBoards.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Alert
                      type="Warning"
                      body={`Warning: You have ${wallBoards.length} wallboard${wallBoards.length === 1 ? '' : 's'} configured. Once you enable SSO, you will not be able to generate any new wallboard access codes while SSO is enabled.`}
                    />
                  </div>
                )}
                <GroupInputCard />
                <UsersGrid selectedGroups={selectedGroups} />
              </div>
              <Row centerAlign spaceBetween>
                {!isSaving && (
                  <Button
                    onClick={onCloseClicked}
                    label="Cancel"
                    type="Tertiary"
                    size="Small"
                  />
                )}
                <Row centerAlign style={{ gap: 8 }}>
                  {mode.hint && (
                    <div style={{ marginRight: 8 }}>
                      <Typography.Body
                        type="Body 12"
                        color="var(--color-text-tertiary)"
                      >
                        {mode.hint}
                      </Typography.Body>
                    </div>
                  )}
                  {mode.secondary && (
                    <Button
                      onClick={() =>
                        mode.secondary && onActionClicked(mode.secondary.action)
                      }
                      label={mode.secondary.label}
                      isDisabled={!isValid}
                      type="Secondary"
                      size="Small"
                    />
                  )}
                  <Button
                    onClick={() => onActionClicked(mode.primary.action)}
                    label={mode.primary.label}
                    isDisabled={!isValid || mode.primary.isDisabled}
                    type="Primary"
                    size="Small"
                  />
                </Row>
              </Row>
            </>
          )}
        </ContentDiv>
      </Modal>
      {UnsavedChangesConfirmationModal}
      {ApproveConfirmationModal}
      {ActivateConfirmationModal}
    </ConfigureMicrosoftSsoContext.Provider>
  );
};

export default ManageGroupsPopupContainer;
